import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCopy } from '../i18n';
import { getUserSubscription, countUserProjects } from '../services/subscriptionService';
import type { Plan, PlanLimits, SubscriptionWithPlan } from '../types';

interface PlanUsage {
  projects: number;
}

interface FeaturesMap {
  [key: string]: string;
}

interface UsePlanLimitsReturn {
  // Estado
  subscription: SubscriptionWithPlan | null;
  plan: Plan | null;
  planName: string;
  limits: PlanLimits;
  features: string[];
  usage: PlanUsage;
  loading: boolean;

  // Verificações
  canCreateProject: () => boolean;
  hasFeature: (feature: string) => boolean;
  canAddMemberToProject: (currentMemberCount: number) => boolean;
  isAtProjectLimit: () => boolean;

  // Informações
  getProjectLimit: () => number | string;
  getProjectUsage: () => number;

  // Ações
  refreshUsage: () => Promise<void>;

  // Constantes
  FEATURES: FeaturesMap;

  // Status
  isPro: boolean;
  isStarter: boolean;
  isFree: boolean;
}

/**
 * Hook para verificar limites e features do plano do usuário
 */
export function usePlanLimits(): UsePlanLimitsReturn {
  const { user } = useAuth();
  const t = useCopy().account.billing;
  const [subscription, setSubscription] = useState<SubscriptionWithPlan | null>(null);
  const [usage, setUsage] = useState<PlanUsage>({ projects: 0 });
  const [loading, setLoading] = useState<boolean>(true);

  // Carregar assinatura e uso
  useEffect(() => {
    if (!user?.id) {
      setSubscription(null);
      setUsage({ projects: 0 });
      setLoading(false);
      return;
    }

    async function loadData(): Promise<void> {
      try {
        const [sub, projectCount] = await Promise.all([
          getUserSubscription(user!.id),
          countUserProjects(user!.id)
        ]);
        setSubscription(sub);
        setUsage({ projects: projectCount });
      } catch (error) {
        console.error('Erro ao carregar dados do plano:', error);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [user?.id]);

  // Plano atual
  const plan: Plan | null = subscription?.plan || null;
  const planName: string = plan?.name || 'free';
  const limits: PlanLimits = plan?.limits || { projects: 2, members_per_project: 3 };
  const features: string[] = plan?.features || [];

  // Verificar se pode criar projeto
  const canCreateProject = useCallback((): boolean => {
    if (!plan) return false;
    const limit = limits.projects;
    if (limit === -1) return true;
    return usage.projects < limit;
  }, [plan, limits.projects, usage.projects]);

  // Verificar se tem feature
  const hasFeature = useCallback((feature: string): boolean => {
    return features.includes(feature);
  }, [features]);

  // Verificar limite de membros por projeto
  const canAddMemberToProject = useCallback((currentMemberCount: number): boolean => {
    const limit = limits.members_per_project;
    if (limit === -1) return true;
    return currentMemberCount < limit;
  }, [limits.members_per_project]);

  // Obter limite de projetos
  const getProjectLimit = useCallback((): number | string => {
    const limit = limits.projects;
    return limit === -1 ? t.unlimited : limit;
  }, [limits.projects, t.unlimited]);

  // Obter uso atual de projetos
  const getProjectUsage = useCallback((): number => {
    return usage.projects;
  }, [usage.projects]);

  // Verificar se está no limite
  const isAtProjectLimit = useCallback((): boolean => {
    const limit = limits.projects;
    if (limit === -1) return false;
    return usage.projects >= limit;
  }, [limits.projects, usage.projects]);

  // Atualizar contagem de projetos (após criar/deletar)
  const refreshUsage = useCallback(async (): Promise<void> => {
    if (!user?.id) return;
    try {
      const projectCount = await countUserProjects(user.id);
      setUsage({ projects: projectCount });
    } catch (error) {
      console.error('Erro ao atualizar uso:', error);
    }
  }, [user?.id]);

  // Features disponíveis por plano. A chave é a que vem do banco; só o rótulo
  // exibido está no dicionário.
  const FEATURES: FeaturesMap = t.planFeatures;

  return {
    // Estado
    subscription,
    plan,
    planName,
    limits,
    features,
    usage,
    loading,

    // Verificações
    canCreateProject,
    hasFeature,
    canAddMemberToProject,
    isAtProjectLimit,

    // Informações
    getProjectLimit,
    getProjectUsage,

    // Ações
    refreshUsage,

    // Constantes
    FEATURES,

    // Status
    isPro: planName === 'professional',
    isStarter: planName === 'starter',
    isFree: planName === 'free'
  };
}
